import MUITextField, { TextFieldProps as MUITextFieldProps } from '@mui/material/TextField'
import { InputHTMLAttributes, Ref } from 'react'
import { useController } from 'react-hook-form'

export type TextFieldProps = MUITextFieldProps & {
  readOnly?: boolean
  showReadOnlyTitle?: boolean
  multiline?: boolean
  rows?: number
  minRows?: number
  maxRows?: number
}
export const TextField = ({ ref, ...props }: TextFieldProps & { ref?: Ref<HTMLDivElement> }) => {
  const { readOnly, showReadOnlyTitle, multiline, rows, minRows, maxRows, slotProps, ...textFieldProps } = props

  const finalSlotProps: TextFieldProps['slotProps'] = slotProps ? { ...slotProps } : { input: {} }
  finalSlotProps.input = {
    ...finalSlotProps.input,
    readOnly,
    multiline,
    rows,
    minRows,
    maxRows,
  }

  const fullWidth = props.fullWidth === undefined ? true : props.fullWidth
  return (
    <MUITextField
      {...textFieldProps}
      fullWidth={fullWidth}
      slotProps={finalSlotProps}
      label={readOnly && showReadOnlyTitle ? props.label + ' (read only)' : props.label}
      ref={ref}
      inputRef={props.inputRef}
      variant={props.variant || 'standard'}
    />
  )
}

type ControlledTextFieldProps = TextFieldProps & {
  name: string
  maxLength?: number
  minLength?: number
}

export const CTextField = ({ ref, ...props }: ControlledTextFieldProps & { ref?: Ref<HTMLDivElement> }) => {
  const { name, maxLength, minLength, ...textFieldProps } = props
  const { field, fieldState } = useController({ name: name, defaultValue: '' })

  const handleInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    // const newValue = event.target.value.replace(/\s+/g, ' ')
    const newValue = event.target.value.replace(/(?<!\n) {2,}(?!\n)/g, ' ')
    field.onChange(newValue)
  }

  const fieldLength = (field.value || '').length

  const slotProps: TextFieldProps['slotProps'] = {
    ...props.slotProps,
    htmlInput: { ...props.slotProps?.htmlInput },
  }

  // 1. Assert the type of slotProps.htmlInput to get access to input-specific attributes
  const inputSlotProps = slotProps?.htmlInput as InputHTMLAttributes<HTMLInputElement>
  const actualMaxLength = inputSlotProps?.maxLength || maxLength
  const actualMinLength = inputSlotProps?.minLength || minLength

  let lengthHelperText = ''

  // Max length given, but no min length
  if (!!actualMaxLength && !actualMinLength && fieldLength >= actualMaxLength) {
    lengthHelperText = `${fieldLength} of ${maxLength} required maximum characters`
  }

  // Min length given, but no max length
  if (!!actualMinLength && !actualMaxLength && fieldLength < actualMinLength) {
    lengthHelperText = `${fieldLength} of ${minLength} required minimum characters`
  }

  // Both min and max length given
  if (!!actualMinLength && !!actualMaxLength && (fieldLength >= actualMaxLength || fieldLength <= actualMinLength)) {
    lengthHelperText = `${fieldLength} of ${minLength}-${maxLength} required characters`
  }

  // Figure out helper text
  let helperText = fieldState.error?.message || props.helperText
  if (lengthHelperText !== '') {
    helperText = `${lengthHelperText} ${helperText ? ' - ' + helperText : ''}`
  }

  const { ...fieldProps } = field
  return (
    <TextField
      {...textFieldProps}
      {...fieldProps}
      inputRef={ref}
      ref={ref}
      error={!!fieldState.error}
      helperText={helperText}
      onChange={handleInputChange}
      onBlur={field.onBlur}
      slotProps={{
        ...slotProps,
        htmlInput: { ...slotProps?.htmlInput, minLength: actualMinLength, maxLength: actualMaxLength },
      }}
    />
  )
}
